/**
 * READ FROM THE SERVER, ONCE PER SCREEN. **Phase 2 §13.**
 *
 * Most screens in this app render `fixturesEnabled() ? fixtureSomething : []`, and the shell paints
 * a ribbon saying so. That was honest while there was no server; it is not a product. This hook is
 * how a screen stops rendering development data.
 *
 * ── THE THREE STATES ARE THREE STATES ───────────────────────────────────────────────────────────
 *
 * `loading`, `loaded` and `failed` are distinct, and a screen must be able to tell them apart. A
 * hook returning `data ?? []` would collapse all three into "empty", so an office whose request
 * failed would be shown *"you have no users"* — which is a false statement about their own office,
 * and the exact class of defect CM-012 and CM-024 were.
 *
 * ── AND WHY THERE IS NO FIXTURE FALLBACK ────────────────────────────────────────────────────────
 *
 * A hook that fell back to fixtures on failure would put development data in front of a customer at
 * the moment the product is least able to say so. Fixtures remain where a screen explicitly asks for
 * them, behind `fixturesEnabled()`, and never as an error path.
 *
 * ── AND WHY `failed` CARRIES A CLASSIFIED FAILURE ───────────────────────────────────────────────
 *
 * The first version of this hook carried NOTHING on failure, on the reasoning that a screen showing a
 * raw error would be disclosing what the gate refused to say. The reasoning was right about raw
 * errors and wrong about the consequence: *"we could not show you your users"* is the same sentence
 * whether the office lacks the capability, whether their session expired, or whether the building's
 * internet is down — and those three need three different people to do three different things.
 *
 * So it carries `ActionFailure` — the SAME taxonomy `classifyActionError` already produces for
 * writes, rendered by the same `<ActionFailureNotice>`. That is not a raw error: it is the server's
 * own chosen disclosure, and a denial still arrives as the contextless `DenyBody` 04 §3.4 mandates.
 * One taxonomy for reads and writes, because two would drift.
 */

import { useCallback, useEffect, useState } from 'react';

import { classifyActionError, type ActionFailure } from './actionOutcome';


export type ServerData<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'loaded'; readonly data: T }
  /** The request was made and did not succeed. NOT "empty" — see this file's header. */
  | { readonly state: 'failed'; readonly failure: ActionFailure };

export interface ServerDataResult<T> {
  readonly result: ServerData<T>;
  /** Re-read. Every mutation on a screen should call this rather than patching local state. */
  readonly reload: () => void;
}

/**
 * Run one read on mount, and again whenever `reload` is invoked.
 *
 * ── IT TAKES A THUNK, AND THE FIRST DRAFT DID NOT ───────────────────────────────────────────────
 *
 * The first version took an operation id and called `callOperation(id, options)` itself. **G-03
 * refused it**, correctly: `callOperation` was then passed a computed id, so every read in the app
 * became invisible to the reachability scan — one hook would have hidden every call site behind
 * itself, which is precisely the opacity the choke point exists to prevent.
 *
 * Taking a thunk puts the literal back at the SCREEN:
 *
 *     const members = useServerData(() => callOperation('tenant.member.read', {}));
 *
 * The scan sees `'tenant.member.read'` where a person reading the screen sees it too. The guardrail
 * found this before it shipped, which is the second time in Phase 2 that a control has refused its
 * own author's convenience.
 *
 * `read` must be STABLE — wrap it in `useCallback`, or write it inline only when its inputs never
 * change — because an unstable thunk re-fires on every render.
 */
export function useServerData<T>(read: () => Promise<T>): ServerDataResult<T> {
  const [result, setResult] = useState<ServerData<T>>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setResult({ state: 'loading' });

    void read().then(
      (data) => {
        if (current) setResult({ state: 'loaded', data });
      },
      (err: unknown) => {
        // CLASSIFIED, never raw. `classifyActionError` is the one place that decides what kind of
        // failure an exception is, and a second decision procedure here would be a second answer to
        // the same question — which is how a refusal starts reading as an outage on one screen.
        if (current) setResult({ state: 'failed', failure: classifyActionError(err) });
      },
    );

    return () => {
      current = false;
    };
  }, [read, attempt]);

  const reload = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  return { result, reload };
}

/**
 * The rows in a named field of the answer, or an empty list while loading or after a failure.
 *
 * Every read route in this product answers with an OBJECT carrying one named collection —
 * `{ members: [...] }`, `{ roles: [...] }` — rather than a bare array, because a bare array has
 * nowhere to grow a second fact and every one of these will eventually carry a count or a cursor.
 * So the common case is "unwrap one field", and this is it.
 *
 * The screen must STILL render the state itself, which is why this returns only the rows and not a
 * verdict. A screen that used this and showed nothing else would be back to "empty means three
 * different things".
 */
export function listOf<T, K extends string>(
  result: ServerData<Record<K, readonly T[]>>,
  field: K,
): readonly T[] {
  return result.state === 'loaded' ? result.data[field] : [];
}

/**
 * TWO READS THAT A SCREEN CANNOT USEFULLY SHOW HALF OF.
 *
 * The role-assignment form is the case this exists for: it is built from the ROLES and from the
 * PEOPLE, and half of it is not a lesser form but a misleading one — a person-picker with no people
 * beside a refused read reads as *"your office has no staff"*.
 *
 * The precedence is deliberate. A FAILURE beats a pending read, because once one half is refused the
 * screen already knows it cannot render the form and waiting to say so only delays the truth; and
 * the FIRST failure wins rather than some merged one, because there is no such thing as a merged
 * refusal — the office needs one reason to act on.
 *
 * Use it only where both halves are genuinely required. A lookup that can DEGRADE — role names beside
 * role codes — should stay separate and degrade, and this would turn it into a blocker.
 */
export function bothOf<A, B>(a: ServerData<A>, b: ServerData<B>): ServerData<readonly [A, B]> {
  if (a.state === 'failed') return a;
  if (b.state === 'failed') return b;
  if (a.state === 'loading' || b.state === 'loading') return { state: 'loading' };
  return { state: 'loaded', data: [a.data, b.data] };
}
